import React from "react";
import { View, StyleSheet } from "react-native";
import { MotiView } from "moti";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import ModuleCatalogueCard from "../../../components/customCoursework/ModuleCatalogueCard/ModuleCatalogueCard";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";

interface Module {
  id: string;
  title?: string;
  description?: string;
  thumbnail?: string;
  aiTutors?: unknown[];
  axioResources?: unknown[];
  assignments?: unknown[];
  projects?: unknown[];
  reviews?: unknown[];
  assessment?: unknown;
  [key: string]: unknown;
}

interface ModuleProgressData {
  id: string;
  title: string;
  progress?: number;
  totalActivities?: number;
  completedActivities?: number;
}

interface ModuleCatalogueCardContainerProps {
  modules: Module[];
  moduleProgressData?: ModuleProgressData[];
  onModulePress: (module: Module) => void;
}

const ModuleCatalogueCardContainer: React.FC<ModuleCatalogueCardContainerProps> = ({
  modules,
  moduleProgressData,
  onModulePress,
}) => {
  const { isLarge } = useResponsiveScreen();

  return (
    <>
      <CustomSpacing size="l" type="vertical" />
      <View role="grid" aria-label="Module cards" style={isLarge ? styles.gridContainer : styles.flexContainer}>
        {modules?.map((module, index) => {
          const moduleProgress =
            moduleProgressData?.find((progressData) => progressData.id === module.id) ??
            moduleProgressData?.find((progressData) => progressData.title === module.title);
          const progress =
            typeof moduleProgress?.progress === "number" ? Math.max(0, Math.min(100, moduleProgress.progress)) : 0;
          return (
            <MotiView
              key={module.id}
              from={{ opacity: 0, translateY: 20, scale: 0.95 }}
              animate={{ opacity: 1, translateY: 0, scale: 1 }}
              delay={400 + index * 100}
              // @ts-expect-error - MotiView transition props have complex typing that's incompatible with simple objects
              transition={{ type: "timing", duration: 500 }}>
              <ModuleCatalogueCard
                module={module}
                onModulePress={onModulePress}
                isLoading={false}
                progress={progress}
                cardWidth={undefined}
              />
            </MotiView>
          );
        })}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  gridContainer: {
    // @ts-expect-error - CSS Grid works on web
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
    rowGap: 28,
    columnGap: 16,
    width: "100%",
  },
  flexContainer: {
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    rowGap: 28,
    columnGap: 16,
    width: "100%",
  },
});

export default ModuleCatalogueCardContainer;
